import React from "react";
import SlidePanel from "../add_customer/SlidePanel";
import { X } from "lucide-react";

const EditPersonalInfo = ({
  id_R,
  editCustomerOpen,
  customerEditDetails,
  setCustomerEditDetails,
  handleEditCustomerDetails,
  handleCloseEditCustomerPersonalInfo,
  handleOpenEmailPrompt,
  handleOpenMobilePrompt,
}) => {
  const formatDate = (date) => {
    return new Date(date)?.toISOString()?.split("T")[0];
  };

  const isFormValid =
    customerEditDetails &&
    Object?.values(customerEditDetails)?.every(
      (val) => val && val?.toString()?.trim() !== "",
    );

  return (
    <SlidePanel
      isOpen={editCustomerOpen}
      onClose={handleCloseEditCustomerPersonalInfo}
    >
      <div className="relative flex flex-col h-full bg-white rounded-[12px]">
        {/* Close Button */}
        <div
          onClick={handleCloseEditCustomerPersonalInfo}
          className="absolute top-0 right-0 cursor-pointer h-[40px] w-[40px] bg-[#FF0000] rounded-tr-[10px] rounded-bl-[20px] flex items-center justify-center z-10"
        >
          <X color="#ffffff" />
        </div>

        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="mt-5 px-5">
            <div className="flex items-center gap-[10px] mb-8">
              <p className="text-[#000] text-xl font-bold">
                Edit Personal Information
              </p>
            </div>

            <p className="text-[15px] text-[#000]">
              ⚠️ Please be aware that changing customer personal information is
              a sensitive operation. Certain details cannot be edited as they
              have been verified through legal identity documents.
            </p>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 mt-8">
            <div className="flex flex-col gap-5">
              {/* First + Last Name */}

              <div className="flex flex-col w-full">
                <label>First Name</label>
                <input
                  type="text"
                  value={customerEditDetails?.firstname}
                  onChange={(e) =>
                    setCustomerEditDetails((prev) => ({
                      ...prev,
                      firstname: e.target.value,
                    }))
                  }
                  className="input"
                  placeholder="Enter first name"
                />
              </div>

              <div className="flex flex-col w-full">
                <label>Last Name</label>
                <input
                  type="text"
                  value={customerEditDetails?.lastname}
                  onChange={(e) =>
                    setCustomerEditDetails((prev) => ({
                      ...prev,
                      lastname: e.target.value,
                    }))
                  }
                  className="input"
                  placeholder="Enter last name"
                />
              </div>

              {/* Middle Name + Gender */}

              <div className="flex flex-col w-full">
                <label>Middle Name</label>
                <input
                  type="text"
                  value={customerEditDetails?.middlename}
                  onChange={(e) =>
                    setCustomerEditDetails((prev) => ({
                      ...prev,
                      middlename: e.target.value,
                    }))
                  }
                  className="input"
                  placeholder="Enter middle name"
                />
              </div>

              <div className="flex flex-col w-full">
                <label>Gender</label>

                <select
                  value={customerEditDetails?.gender || ""}
                  onChange={(e) =>
                    setCustomerEditDetails((prev) => ({
                      ...prev,
                      gender: e?.target?.value,
                    }))
                  }
                  className="input"
                >
                  {!customerEditDetails?.gender && (
                    <option value="">Select gender</option>
                  )}

                  {customerEditDetails?.gender && (
                    <option value={customerEditDetails?.gender}>
                      {customerEditDetails?.gender?.charAt(0)?.toUpperCase() +
                        customerEditDetails?.gender?.slice(1)}
                    </option>
                  )}

                  {customerEditDetails?.gender !== "male" && (
                    <option value="male">Male</option>
                  )}

                  {customerEditDetails?.gender !== "female" && (
                    <option value="female">Female</option>
                  )}

                  {customerEditDetails?.gender !== "other" && (
                    <option value="other">Other</option>
                  )}
                </select>
              </div>

              {/* DOB + Citizenship */}

              <div className="flex flex-col w-full">
                <label>Date of Birth</label>
                <input
                  type="date"
                  value={
                    customerEditDetails?.dob &&
                    formatDate(customerEditDetails?.dob)
                  }
                  onChange={(e) =>
                    setCustomerEditDetails((prev) => ({
                      ...prev,
                      dob: e.target.value,
                    }))
                  }
                  className="input"
                />
              </div>

              <div className="flex flex-col w-full">
                <label>Citizenship</label>
                <input
                  type="text"
                  value={customerEditDetails?.citizenship}
                  onChange={(e) =>
                    setCustomerEditDetails((prev) => ({
                      ...prev,
                      citizenship: e.target.value,
                    }))
                  }
                  className="input"
                  placeholder="Enter citizenship"
                />
              </div>

              {/* ID + Username */}

              <div className="flex flex-col w-full">
                <label>National ID Number</label>
                <input
                  type="text"
                  value={customerEditDetails?.identification}
                  onChange={(e) =>
                    setCustomerEditDetails((prev) => ({
                      ...prev,
                      identification: e.target.value,
                    }))
                  }
                  className="input"
                  placeholder="Enter national ID"
                />
              </div>

              <div className="flex flex-col w-full">
                <label>Username</label>
                <input
                  type="text"
                  value={customerEditDetails?.username}
                  onChange={(e) =>
                    setCustomerEditDetails((prev) => ({
                      ...prev,
                      username: e.target.value,
                    }))
                  }
                  className="input"
                  placeholder="Enter username"
                />
              </div>

              {/* Email + Phone */}

              <div className="flex flex-col w-full">
                <label>Email</label>
                <input
                  type="text"
                  readOnly
                  onClick={handleOpenEmailPrompt}
                  value={customerEditDetails?.email}
                  className="input cursor-pointer"
                  placeholder="Click to edit email"
                />
              </div>

              <div className="flex flex-col w-full">
                <label>Phone Number</label>
                <input
                  type="text"
                  readOnly
                  onClick={handleOpenMobilePrompt}
                  value={customerEditDetails?.mobileno}
                  className="input cursor-pointer"
                  placeholder="Click to edit phone number"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4">
            <button
              onClick={handleEditCustomerDetails}
              disabled={!isFormValid}
              className={`w-full text-white px-6 py-3 rounded flex items-center justify-center
                ${
                  isFormValid
                    ? "bg-[#074073]"
                    : "bg-[#D9D9D9] cursor-not-allowed"
                }`}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </SlidePanel>
  );
};

export default EditPersonalInfo;
